import i18n from '../lib/i18n'

/**
 * Performance Thresholds and Color Coding Utility
 * Determines performance status and colors for KPI metrics
 */

/**
 * Performance threshold definitions
 */
const THRESHOLDS = {
  profitMargin: {
    excellent: 20, // 20%+ is excellent
    good: 10,      // 10-20% is good
    warning: 0,    // 0-10% is warning
    // Below 0% is poor
  },
  conversionRate: {
    excellent: 15, // 15%+ is excellent
    good: 8,       // 8-15% is good
    warning: 3,    // 3-8% is warning
    // Below 3% is poor
  },
  revenueGrowth: {
    excellent: 20, // 20%+ growth is excellent
    good: 10,      // 10-20% growth is good
    warning: 0,    // 0-10% growth is warning
    // Negative growth is poor
  },
  expenseRatio: {
    excellent: 60, // Below 60% is excellent (lower is better)
    good: 80,      // 60-80% is good
    warning: 90,   // 80-90% is warning
    // Above 90% is poor
  }
}

/**
 * Color schemes for different performance levels
 */
const COLORS = {
  excellent: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: 'text-green-600',
    indicator: 'bg-green-500'
  },
  good: {
    bg: 'bg-blue-50',
    border: 'border-blue-200', 
    text: 'text-blue-800',
    icon: 'text-blue-600',
    indicator: 'bg-blue-500'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: 'text-yellow-600',
    indicator: 'bg-yellow-500'
  },
  poor: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: 'text-red-600',
    indicator: 'bg-red-500'
  },
  neutral: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-800',
    icon: 'text-gray-600',
    indicator: 'bg-gray-500'
  }
}

/**
 * Get performance status for profit margin
 */
export const getProfitMarginStatus = (profitMargin) => {
  if (profitMargin >= THRESHOLDS.profitMargin.excellent) return 'excellent'
  if (profitMargin >= THRESHOLDS.profitMargin.good) return 'good'
  if (profitMargin >= THRESHOLDS.profitMargin.warning) return 'warning'
  return 'poor'
}

/**
 * Get performance status for conversion rate
 */
export const getConversionRateStatus = (conversionRate) => {
  if (conversionRate >= THRESHOLDS.conversionRate.excellent) return 'excellent'
  if (conversionRate >= THRESHOLDS.conversionRate.good) return 'good'
  if (conversionRate >= THRESHOLDS.conversionRate.warning) return 'warning'
  return 'poor'
}

/**
 * Get performance status for revenue growth
 */
export const getRevenueGrowthStatus = (revenueGrowth) => {
  if (revenueGrowth >= THRESHOLDS.revenueGrowth.excellent) return 'excellent'
  if (revenueGrowth >= THRESHOLDS.revenueGrowth.good) return 'good'
  if (revenueGrowth >= THRESHOLDS.revenueGrowth.warning) return 'warning'
  return 'poor'
}

/**
 * Get performance status for expense ratio (lower is better)
 */
export const getExpenseRatioStatus = (expenseRatio) => {
  if (expenseRatio <= THRESHOLDS.expenseRatio.excellent) return 'excellent'
  if (expenseRatio <= THRESHOLDS.expenseRatio.good) return 'good'
  if (expenseRatio <= THRESHOLDS.expenseRatio.warning) return 'warning'
  return 'poor'
}

/**
 * Get color scheme for performance status
 */
export const getStatusColors = (status) => {
  return COLORS[status] || COLORS.neutral
}

/**
 * Helper to treat floating point numbers as equal when extremely close.
 */
const isEffectivelyEqual = (a, b, epsilon = 1e-6) => {
  if (a === null || a === undefined) return false
  if (b === null || b === undefined) return false
  return Math.abs(Number(a) - Number(b)) <= epsilon
}

/**
 * Status for raw currency metrics where we care about month-over-month direction.
 * Neutral = unchanged or no previous data.
 */
export const getMoMStatus = (currentValue, previousValue) => {
  if (currentValue === null || currentValue === undefined) return 'neutral'

  // If we don't have previous data, still reflect whether the metric is positive.
  // Neutral should indicate "no change" or "no meaningful movement".
  if (previousValue === null || previousValue === undefined) {
    const numeric = Number(currentValue)
    if (numeric > 0) return 'excellent'
    if (numeric < 0) return 'poor'
    return 'neutral'
  }

  if (isEffectivelyEqual(currentValue, previousValue)) return 'neutral'

  return Number(currentValue) > Number(previousValue) ? 'excellent' : 'poor'
}

/**
 * Get performance status for any metric
 */
export const getMetricStatus = (metricType, value, previousValue = undefined) => {
  switch (metricType) {
    case 'profitMargin':
      return getProfitMarginStatus(value)
    case 'conversionRate':
      return getConversionRateStatus(value)
    case 'revenueGrowth':
      return getRevenueGrowthStatus(value)
    case 'expenseRatio':
      return getExpenseRatioStatus(value)
    case 'revenue':
    case 'netProfit':
      return getMoMStatus(value, previousValue)
    default:
      return 'neutral'
  }
}

/**
 * Get trend direction and change text
 */
export const getTrendInfo = (currentValue, previousValue) => {
  if (previousValue === null || previousValue === undefined) {
    return {
      direction: 'neutral',
      change: null,
      changeText: i18n.t('kpi.noPreviousData', { ns: 'dashboard' })
    }
  }

  const change = currentValue - previousValue
  const changePercent = previousValue !== 0 ? (change / Math.abs(previousValue)) * 100 : 0

  if (changePercent > 5) {
    return {
      direction: 'up',
      change: changePercent,
      changeText: `+${changePercent.toFixed(1)}%`
    }
  }

  if (changePercent < -5) {
    return {
      direction: 'down',
      change: changePercent,
      changeText: `${changePercent.toFixed(1)}%`
    }
  }

  return {
    direction: 'stable',
    change: changePercent,
    changeText: `${changePercent.toFixed(1)}%`
  }
}

/**
 * Get metric explanations and tips
 */
export const getMetricInfo = (metricType) => {
  const info = {
    profitMargin: {
      title: i18n.t('kpi.profitMargin.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.profitMargin.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.profitMargin.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.profitMargin.thresholds', { ns: 'dashboard' })
    },
    conversionRate: {
      title: i18n.t('kpi.conversionRate.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.conversionRate.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.conversionRate.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.conversionRate.thresholds', { ns: 'dashboard' })
    },
    revenueGrowth: {
      title: i18n.t('kpi.revenueGrowth.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.revenueGrowth.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.revenueGrowth.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.revenueGrowth.thresholds', { ns: 'dashboard' })
    },
    expenseRatio: {
      title: i18n.t('kpi.expenseRatio.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.expenseRatio.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.expenseRatio.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.expenseRatio.thresholds', { ns: 'dashboard' })
    },
    revenue: {
      title: i18n.t('kpi.revenue.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.revenue.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.revenue.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.revenue.thresholds', { ns: 'dashboard' })
    },
    netProfit: {
      title: i18n.t('kpi.netProfit.title', { ns: 'dashboard' }),
      description: i18n.t('kpiInfo.netProfit.description', { ns: 'dashboard' }),
      tip: i18n.t('kpiInfo.netProfit.tip', { ns: 'dashboard' }),
      thresholds: i18n.t('kpiInfo.netProfit.thresholds', { ns: 'dashboard' })
    }
  }

  return info[metricType] || {
    title: i18n.t('kpiInfo.default.title', { ns: 'dashboard' }),
    description: i18n.t('kpiInfo.default.description', { ns: 'dashboard' }),
    tip: i18n.t('kpiInfo.default.tip', { ns: 'dashboard' }),
    thresholds: i18n.t('kpiInfo.default.thresholds', { ns: 'dashboard' })
  }
}
